import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiJson } from './client';

export interface Notification {
  id: string;
  uuid: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  icon: string;
  severity: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  actionUrl: string | null;
}

export function useNotifications(onlyUnread = false) {
  return useQuery({
    queryKey: ['notifications', onlyUnread ? 'unread' : 'all'],
    queryFn: () =>
      api.get(`notifications${onlyUnread ? '?onlyUnread=1' : ''}`).json<Notification[]>(),
    refetchInterval: 60_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => api.get('notifications/unread-count').json<{ count: number }>(),
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) =>
      apiJson<{ success: boolean }>(`notifications/${uuid}/read`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ success: boolean }>('notifications/read-all', { method: 'POST', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) =>
      apiJson<{ success: boolean }>(`notifications/${uuid}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
